import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

// TODO(DS): replace with @tb/icon when DS icon set is available
function AlertIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4" />
      <path d="M12 17.5v.5" />
    </svg>
  );
}

/**
 * Error slot — always rendered (min-height reserved), so an error appearing never
 * shifts the layout, unlike a conditional `{errors.x && <p>}`.
 *
 * `className` is for surfaces where `text-destructive` doesn't hold contrast —
 * forms over photography, which the Tech Spec requires to be pure white.
 */
export function FieldError({ id, className, children }: { id?: string; className?: string; children?: ReactNode }) {
  return (
    <span
      id={id}
      role={children ? "alert" : undefined}
      className={cn(
        "mt-[7px] block min-h-[13px] text-[11px] leading-[1.2] font-medium tracking-[0.04em] text-destructive",
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Hint and error in one slot — neutral by default, an error (color + icon) when `error` is
 * true. Unlike `FieldError`, it doubles as the field's default helper text.
 */
export function FieldHelp({ id, error, children }: { id?: string; error?: boolean; children: ReactNode }) {
  return (
    <p
      id={id}
      className={`flex min-h-[17px] items-center gap-[7px] text-[12px] tracking-[0.06em] ${
        error ? "text-destructive" : "text-fg-muted"
      }`}
    >
      {error && <AlertIcon />}
      {children}
    </p>
  );
}
